// ========================================================================
// 🏥 SERVICIO DE GESTIÓN DE REGISTROS DE SALUD - ACTUALIZADO
// ========================================================================

import {
  API_BASE_HEALTHLOG_URL,
  API_HEALTHLOG_LIST,
  API_HEALTHLOG_DETAILS,
  API_HEALTHLOG_CREATE,
  API_HEALTHLOG_UPDATE,
  API_HEALTHLOG_DELETE,
  API_HEALTHLOG_BY_USER,
  API_HEALTHLOG_BULK_ACTION,
  API_HEALTHLOG_EXPORT,
  API_HEALTHLOG_STATISTICS,
  API_HEALTHLOG_VITALS,
  API_HEALTHLOG_BODY_MEASUREMENTS,
  API_HEALTHLOG_SUGAR_PROFILE,
  API_HEALTHLOG_LIVER_PROFILE,
  API_HEALTHLOG_KIDNEY_PROFILE,
  API_HEALTHLOG_LIPID_PROFILE,
  API_HEALTHLOG_CYCLE_TRACKING,
  API_HEALTHLOG_PREGNANCY_TRACKING,
  API_HEALTHLOG_GET_VITALS,
  API_HEALTHLOG_GET_LATEST_VITALS
} from '../../../../helpers/urlHelper'; // Ajusta la ruta según tu estructura

// ========================================================================
// FUNCIONES HELPER PARA CONSTRUIR URLs
// ========================================================================

// URLs principales CRUD
export const getHealthLogListUrl = () => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_LIST}`;
export const getHealthLogDetailsUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_DETAILS}/${id}`;
export const getHealthLogCreateUrl = () => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_CREATE}`;
export const getHealthLogUpdateUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_UPDATE}/${id}`;
export const getHealthLogDeleteUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_DELETE}/${id}`;
export const getHealthLogByUserUrl = (userId) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_BY_USER}/${userId}`;

// URLs adicionales
export const getHealthLogBulkActionUrl = () => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_BULK_ACTION}`;
export const getHealthLogExportUrl = () => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_EXPORT}`;
export const getHealthLogStatisticsUrl = () => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_STATISTICS}`;

// URLs específicas para subsecciones (PUT) - Según las rutas del backend
export const getHealthLogVitalsUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_VITALS}/${id}/vitals`;
export const getHealthLogBodyMeasurementsUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_BODY_MEASUREMENTS}/${id}/body-measurements`;
export const getHealthLogSugarProfileUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_SUGAR_PROFILE}/${id}/sugar-profile`;
export const getHealthLogLiverProfileUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_LIVER_PROFILE}/${id}/liver-profile`;
export const getHealthLogKidneyProfileUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_KIDNEY_PROFILE}/${id}/kidney-profile`;
export const getHealthLogLipidProfileUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_LIPID_PROFILE}/${id}/lipid-profile`;
export const getHealthLogCycleTrackingUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_CYCLE_TRACKING}/${id}/cycle-tracking`;
export const getHealthLogPregnancyTrackingUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_PREGNANCY_TRACKING}/${id}/pregnancy-tracking`;

// URLs para obtener datos específicos (GET)
export const getHealthLogVitalsDataUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_GET_VITALS}/${id}/vitals`;
export const getHealthLogLatestVitalsUrl = (id) => `${API_BASE_HEALTHLOG_URL}${API_HEALTHLOG_GET_LATEST_VITALS}/${id}/latest-vitals`;

// ========================================================================
// FUNCIONES DE SERVICIO PARA OPERACIONES CRUD
// ========================================================================

// Función helper para manejar errores HTTP
const handleApiError = (error) => {
  console.error('Health Log API Error:', error);
  
  // Si es un error de fetch (red, servidor no disponible, etc.)
  if (!error.response) {
    throw {
      success: false,
      message: error.message || 'Error de conexión con el servidor',
      status: 0,
      data: null
    };
  }
  
  // Error con respuesta del servidor
  throw {
    success: false,
    message: error.response?.data?.message || `Error ${error.response?.status}`,
    status: error.response?.status || 500,
    data: error.response?.data || null
  };
};

// Función helper para hacer peticiones HTTP
const makeRequest = async (url, options = {}) => {
  try {
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        // Aquí puedes agregar headers de autenticación si es necesario
        // 'Authorization': `Bearer ${getToken()}`
      }
    };

    const response = await fetch(url, { ...defaultOptions, ...options });
    
    // Parsear respuesta JSON
    const data = await response.json();
    
    if (!response.ok) {
      throw {
        response: {
          status: response.status,
          data: data
        }
      };
    }

    return data;
  } catch (error) {
    return handleApiError(error);
  }
};

// ========================================================================
// OPERACIONES CRUD PRINCIPALES
// ========================================================================

// GET - Obtener todos los health logs
export const getAllHealthLogs = async () => {
  return await makeRequest(getHealthLogListUrl(), {
    method: 'GET'
  });
};

// GET - Obtener health log por ID
export const getHealthLogById = async (id) => {
  if (!id) {
    throw { success: false, message: 'ID es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogDetailsUrl(id), {
    method: 'GET'
  });
};

// GET - Obtener health logs por usuario
export const getHealthLogsByUser = async (userId) => {
  if (!userId) {
    throw { success: false, message: 'ID de usuario es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogByUserUrl(userId), {
    method: 'GET'
  });
};

// POST - Crear nuevo health log
export const createHealthLog = async (healthLogData) => {
  if (!healthLogData || !healthLogData.userId) {
    throw { success: false, message: 'Datos del health log y userId son requeridos', status: 400 };
  }

  return await makeRequest(getHealthLogCreateUrl(), {
    method: 'POST',
    body: JSON.stringify(healthLogData)
  });
};

// PUT - Actualizar health log completo
export const updateHealthLog = async (id, healthLogData) => {
  if (!id) {
    throw { success: false, message: 'ID es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogUpdateUrl(id), {
    method: 'PUT',
    body: JSON.stringify(healthLogData)
  });
};

// DELETE - Eliminar health log
export const deleteHealthLog = async (id) => {
  if (!id) {
    throw { success: false, message: 'ID es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogDeleteUrl(id), {
    method: 'DELETE'
  });
};

// ========================================================================
// FUNCIONES ESPECÍFICAS PARA SUBSECCIONES
// ========================================================================

// PUT - Actualizar vitales
// Métodos específicos para actualizar subsecciones (mantener los que ya tienes)

// PUT - Actualizar vitales específicos
export const updateVitals = async (id, vitalsData) => {
  if (!id || !vitalsData) {
    throw { success: false, message: 'ID y datos de vitales son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/vitals`, {
    method: 'PUT',
    body: JSON.stringify(vitalsData)
  });
};

// PUT - Actualizar vital específico por índice
export const updateSpecificVital = async (id, vitalIndex, vitalsData) => {
  if (!id || vitalIndex === undefined || !vitalsData) {
    throw { success: false, message: 'ID, índice del vital y datos son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/vitals/${vitalIndex}`, {
    method: 'PUT',
    body: JSON.stringify(vitalsData)
  });
};

// PUT - Actualizar medidas corporales
export const updateBodyMeasurements = async (id, measurementsData) => {
  if (!id || !measurementsData) {
    throw { success: false, message: 'ID y datos de medidas corporales son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/body-measurements`, {
    method: 'PUT',
    body: JSON.stringify(measurementsData)
  });
};

// PUT - Actualizar perfil de azúcar
export const updateSugarProfile = async (id, sugarData) => {
  if (!id || !sugarData) {
    throw { success: false, message: 'ID y datos de perfil de azúcar son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/sugar-profile`, {
    method: 'PUT',
    body: JSON.stringify(sugarData)
  });
};

// PUT - Actualizar perfil hepático
export const updateLiverProfile = async (id, liverData) => {
  if (!id || !liverData) {
    throw { success: false, message: 'ID y datos de perfil hepático son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/liver-profile`, {
    method: 'PUT',
    body: JSON.stringify(liverData)
  });
};

// PUT - Actualizar perfil renal
export const updateKidneyProfile = async (id, kidneyData) => {
  if (!id || !kidneyData) {
    throw { success: false, message: 'ID y datos de perfil renal son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/kidney-profile`, {
    method: 'PUT',
    body: JSON.stringify(kidneyData)
  });
};

// PUT - Actualizar perfil lipídico
export const updateLipidProfile = async (id, lipidData) => {
  if (!id || !lipidData) {
    throw { success: false, message: 'ID y datos de perfil lipídico son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/lipid-profile`, {
    method: 'PUT',
    body: JSON.stringify(lipidData)
  });
};

// PUT - Actualizar seguimiento menstrual
export const updateCycleTracking = async (id, cycleData) => {
  if (!id || !cycleData) {
    throw { success: false, message: 'ID y datos de seguimiento menstrual son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/cycle-tracking`, {
    method: 'PUT',
    body: JSON.stringify(cycleData)
  });
};

// PUT - Actualizar seguimiento de embarazo
export const updatePregnancyTracking = async (id, pregnancyData) => {
  if (!id || !pregnancyData) {
    throw { success: false, message: 'ID y datos de seguimiento de embarazo son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/pregnancy-tracking`, {
    method: 'PUT',
    body: JSON.stringify(pregnancyData)
  });
};

// PUT - Actualizar vital específico
// export const updateSpecificVital = async (id, vitalIndex, vitalsData) => {
//   if (!id || vitalIndex === undefined || !vitalsData) {
//     throw { success: false, message: 'ID, índice del vital y datos son requeridos', status: 400 };
//   }

//   return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/vitals/${vitalIndex}`, {
//     method: 'PUT',
//     body: JSON.stringify(vitalsData)
//   });
// };

// DELETE - Eliminar vital específico
export const deleteSpecificVital = async (id, vitalIndex) => {
  if (!id || vitalIndex === undefined) {
    throw { success: false, message: 'ID e índice del vital son requeridos', status: 400 };
  }

  return await makeRequest(`${API_BASE_HEALTHLOG_URL}/${id}/vitals/${vitalIndex}`, {
    method: 'DELETE'
  });
};
// ========================================================================
// FUNCIONES ADICIONALES PARA OBTENER DATOS ESPECÍFICOS
// ========================================================================

// GET - Obtener solo vitales de un health log
export const getHealthLogVitals = async (id) => {
  if (!id) {
    throw { success: false, message: 'ID es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogVitalsDataUrl(id), {
    method: 'GET'
  });
};

// GET - Obtener vitales más recientes
export const getLatestVitals = async (id) => {
  if (!id) {
    throw { success: false, message: 'ID es requerido', status: 400 };
  }

  return await makeRequest(getHealthLogLatestVitalsUrl(id), {
    method: 'GET'
  });
};

// ========================================================================
// EXPORTAR TODAS LAS FUNCIONES
// ========================================================================

export default {
  // CRUD principales
  getAllHealthLogs,
  getHealthLogById,
  getHealthLogsByUser,
  createHealthLog,
  updateHealthLog,
  deleteHealthLog,
  
  // Funciones específicas para subsecciones
  updateVitals,
  updateBodyMeasurements,
  updateSugarProfile,
  updateLiverProfile,
  updateKidneyProfile,
  updateLipidProfile,
  updateCycleTracking,
  updatePregnancyTracking,
  
  // Funciones adicionales
  getHealthLogVitals,
  getLatestVitals,
  
  // URLs helpers (por si los necesitas externamente)
  getHealthLogListUrl,
  getHealthLogDetailsUrl,
  getHealthLogCreateUrl,
  getHealthLogUpdateUrl,
  getHealthLogDeleteUrl,
  getHealthLogByUserUrl,
  getHealthLogBulkActionUrl,
  getHealthLogExportUrl,
  getHealthLogStatisticsUrl,
  getHealthLogVitalsUrl,
  getHealthLogBodyMeasurementsUrl,
  getHealthLogSugarProfileUrl,
  getHealthLogLiverProfileUrl,
  getHealthLogKidneyProfileUrl,
  getHealthLogLipidProfileUrl,
  getHealthLogCycleTrackingUrl,
  getHealthLogPregnancyTrackingUrl,
  getHealthLogVitalsDataUrl,
  getHealthLogLatestVitalsUrl,
  updateSpecificVital,
  deleteSpecificVital
};

// ========================================================================
// EJEMPLOS DE USO:
// ========================================================================

/*
// IMPORTAR EL SERVICE
import healthLogService from './path/to/healthLogService';
// O importar funciones específicas:
// import { getAllHealthLogs, createHealthLog, updateVitals } from './path/to/healthLogService';

// EJEMPLOS DE USO:

// 1. Obtener todos los health logs
try {
  const response = await healthLogService.getAllHealthLogs();
  if (response.success) {
    console.log('Health logs:', response.data);
    console.log('Total:', response.count);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 2. Obtener health log específico
try {
  const response = await healthLogService.getHealthLogById("64a1b2c3d4e5f6789abcdef0");
  if (response.success) {
    console.log('Health log:', response.data);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 3. Crear nuevo health log
try {
  const newHealthLogData = {
    userId: "64a1b2c3d4e5f6789abcdef1",
    vitals: [{
      bloodPressure: { systolic: 120, diastolic: 80 },
      heartRate: 72,
      temperature: 36.5
    }],
    bodyMeasurements: [{
      height: 175,
      weight: 70
    }]
  };
  
  const response = await healthLogService.createHealthLog(newHealthLogData);
  if (response.success) {
    console.log('Health log creado:', response.data);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 4. Actualizar vitales específicos
try {
  const vitalsData = {
    bloodPressure: { systolic: 130, diastolic: 85 },
    heartRate: 75,
    temperature: 36.7,
    oxygenSaturation: 98
  };
  
  const response = await healthLogService.updateVitals("64a1b2c3d4e5f6789abcdef0", vitalsData);
  if (response.success) {
    console.log('Vitales actualizados:', response.data);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 5. Obtener health logs de un usuario específico
try {
  const response = await healthLogService.getHealthLogsByUser("64a1b2c3d4e5f6789abcdef1");
  if (response.success) {
    console.log('Health logs del usuario:', response.data);
    console.log('Total del usuario:', response.count);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 6. Obtener solo los vitales de un health log
try {
  const response = await healthLogService.getHealthLogVitals("64a1b2c3d4e5f6789abcdef0");
  if (response.success) {
    console.log('Vitales:', response.data);
  }
} catch (error) {
  console.error('Error:', error.message);
}

// 7. Obtener los vitales más recientes
try {
  const response = await healthLogService.getLatestVitals("64a1b2c3d4e5f6789abcdef0");
  if (response.success) {
    console.log('Últimos vitales:', response.data);
  }
} catch (error) {
  console.error('Error:', error.message);
}
*/